import { injectable, inject } from 'inversify';
import { ok, err } from '../../../tracking-api';
import { CollectionDataStore } from './collection-data-store';
import { ErrorHandlingScorm2004Client, ErrorHandlingScorm2004ClientSymbol,
         Scorm2004CommandError } from '@/app/tracking/scorm/2004/client';

const successStatusKeys = ['passed', 'failed', 'unknown'] as const;
type SuccessStatus = typeof successStatusKeys[number];

const completionStatusKeys = ['completed', 'incomplete', 'not attempted', 'unknown'] as const;
type CompletionStatus = typeof completionStatusKeys[number];

export interface Scorm2004ObjectiveDetails {
  id: string;
  score: {
    min: number | '',
    raw: number | '',
    max: number | '',
    scaled: number | ''
  };
  completionStatus: CompletionStatus;
  successStatus: SuccessStatus;
  progressMeasure: number;
  description: string;
}

@injectable()
export class Scorm2004ObjectiveDataStore {

  private collection: CollectionDataStore<Scorm2004ObjectiveDetails, Scorm2004CommandError>;

  constructor(
    @inject(ErrorHandlingScorm2004ClientSymbol)
    private readonly client: ErrorHandlingScorm2004Client) {

    this.collection = new CollectionDataStore(() => this.queryCount(),
                                              (index: number) => this.queryId(index),
                                              (index: number) => this.loadItem(index),
                                              (index: number,
                                               details: Scorm2004ObjectiveDetails) => this.saveItem(index, details)
                                             );
  }

  public async upsert(details: Scorm2004ObjectiveDetails) {
    return this.collection.upsert(details);
  }

  public async queryLast(id: string) {
    return await this.collection.queryLast(id);
  }

  private async queryCount() {
    const countResult = await this.client.getValue('cmi.objectives._count');
    if (!countResult.success) {
      return err(countResult.error);
    }

    const parsed = Number.parseInt(countResult.value, 10);
    if (isNaN(parsed)) {
      throw new Error(`Invalid count ${countResult.value}`);
    }

    return ok(parsed);
  }

  private async queryId(index: number) {
    return await this.client.getValue(this.objectivePath(index, 'id'));
  }

  private async loadItem(index: number) {

    const mapper: Mapper<LoadTarget> = {
      id: [ 'id', (x) => x ],
      scoreMin: [ 'score.min', (x) => parseScore(x) ],
      scoreMax: [ 'score.max', (x) => parseScore(x) ],
      scoreRaw: [ 'score.raw', (x) => parseScore(x) ],
      scoreScaled: [ 'score.scaled', (x) => parseScore(x) ],
      completionStatus: [ 'completion_status', (x) =>
            parseEnum<CompletionStatus>(x, completionStatusKeys, 'unknown')],
      successStatus: [ 'success_status', (x) =>
            parseEnum<SuccessStatus>(x, successStatusKeys, 'unknown')],
      progressMeasure: [ 'progress_measure', (x) => x ? parseFloat(x) : 0],
      description: [ 'description', (x) => x ]
    };

    const target: Partial<LoadTarget> = {};
    for (const key in mapper) {
      const typedKey = key as keyof typeof mapper;
      if (!mapper[typedKey]) { continue; }

      const item = mapper[typedKey];
      const getResult = await this.client.getValue(this.objectivePath(index, item[0]));
      if (!getResult.success) {
        return err(getResult.error);
      }

      target[typedKey] = item[1](getResult.value) as any;
    }

    const details: Scorm2004ObjectiveDetails = {
      id: target.id!,
      score: {
        min: target.scoreMin!,
        max: target.scoreMax!,
        raw: target.scoreRaw!,
        scaled: target.scoreScaled!
      },
      completionStatus: target.completionStatus!,
      successStatus: target.successStatus!,
      progressMeasure: target.progressMeasure!,
      description: target.description!
    };

    return ok(details);

    function parseScore(input: string) {
      if (input === '') { return ''; }

      return parseFloat(input);
    }

    function parseEnum<T extends string>(input: string, items: readonly T[], def: T): T {
      if (items.indexOf(input as T) >= 0) {
        return input as T;
      }

      return def;
    }
  }

  private saveItem(index: number, details: Scorm2004ObjectiveDetails) {

    const items = [
      { path: 'id', value: details.id },
      { path: 'score.min', value: details.score.min },
      { path: 'score.max', value: details.score.max },
      { path: 'score.raw', value: details.score.raw },
      { path: 'score.scaled', value: details.score.scaled },
      { path: 'success_status', value: details.successStatus },
      { path: 'completion_status', value: details.completionStatus },
      { path: 'progress_measure', value: details.progressMeasure },
      { path: 'description', value: details.description.substr(0, 250) }
    ];

    for (const item of items) {
      this.client.setValue(this.objectivePath(index, item.path),
                                             item.value.toString());
    }

    this.client.commit();

    return ok(true as true);
  }

  private objectivePath(index: number, member: string) {
    return `cmi.objectives.${index}.${member}`;
  }
}

interface LoadTarget {
  id: string;
  scoreMin: number | '';
  scoreMax: number | '';
  scoreRaw: number | '';
  scoreScaled: number | '';
  completionStatus: CompletionStatus;
  successStatus: SuccessStatus;
  progressMeasure: number;
  description: string;
}

type Mapper<T> = {
  [P in keyof T]: [ string, (input: string) => T[P] ]
};
